import React, { useEffect, useState } from 'react'
import { ButtonDropdown, DropdownItem, DropdownMenu, DropdownToggle } from 'reactstrap'
import { useDispatch, useSelector } from 'react-redux'
import { toast } from 'react-toastify'

import usePrevious from '../../../hooks/UsePrevious'
import { createFranchescaAssessmentsBySiteId, downloadFranchescaAssessmentBySiteId, getFranchescaAssessmentsBySiteId, updateFranchescaAssessment } from '../../../utils/Sites'
import FranchescaAssessmentListItem from './FranchescaAssessmentListItem'
import introJs from 'intro.js'

const INITIAL_STATE = {
    assessments: [],
    loadingId: null,
    createFranchescaDropdownOpen: false,
    draftAssessment: {}
}

function FranchescaAssessments(){
    const dispatch = useDispatch()

    const [state, setState] = useState(INITIAL_STATE)

    const model = useSelector(state => state.site.model)

    const prevModel = usePrevious(model)

    const runIntroTutorial = () => {
        const introJsProcess = introJs()
        let steps = [{
            element: document.querySelector('#createAssessmentBtn'),
            title: 'Create Franchesca Assessment',
            intro: "A Franchesca assessment can be created here, with the options of 'Initial Scan' and 'Secret Shopping'",
            position: 'bottom'
        },{
            element: document.querySelector('#franchescaAssessmentSidebar'),
            title: 'Franchesca Assessment Basics',
            intro: "Each Franchesca assessment will appear in this sidebar section with the assessment type as the title, along with the assessment status, findings count by status, the ability to edit (when applicable) and download",
            position: 'left'
        }, {
            element: document.querySelector('#franchescaAssessmentSidebar'),
            title: 'Initial Scan Creation',
            intro: "On Initial Scan creation, the site associated to the assessment will have its website analyzed via Franchesca AI and the assessment would then be marked as 'Complete'",
            position: 'left'
        }, {
            element: document.querySelector('#franchescaAssessmentSidebar'),
            title: 'Secret Shopping Creation',
            intro: "On Secret Shopping creation, the site associated to the assessment will have its main FranDev form submitted via Franchesca AI and track form submission data. The assessment would then be marked as 'Verifying'",
            position: 'left'
        }, {
            element: document.querySelector('#franchescaAssessmentSidebar'),
            title: 'Secret Shopping Verification Process',
            intro: "Once Secret Shopping form submission is successfully completed, the email and phone number are polled for the intro message in each respective source. Once established that sms results and email results are available (or timed out due to no expected results), the assessment would be marked as 'Verified'",
            position: 'left'
        }, {
            element: document.querySelector('#franchescaAssessmentSidebar'),
            title: 'Secret Shopping Final Analysis',
            intro: "Upon the Secret Shopping scan being marked as 'Verified', 'First Open Appointment' can be set by clicking on the edit button to reveal the section where that input exists. Once set, click 'Save' and the final analysis will take place which will grade email and SMS outreach and efficacy. Then, the assessment would be marked as 'Complete'",
            position: 'left'
        }]

        introJsProcess.setOptions({
            steps   
        })

        introJsProcess.start()
    }

    const onDownload = async(assessment) => {
        setState(prev => ({
            ...prev,
            loadingId: assessment.assessmentId
        }))
        try {
            const { response } = await downloadFranchescaAssessmentBySiteId(assessment.assessmentId)

            const url = window.URL.createObjectURL(new Blob([response], { type: 'application/pdf' }));
            const a = document.createElement('a');
            a.style.display = 'none';
            a.href = url;
            
            a.download = 'Franchesca_Assessment_Report.pdf';
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
        } catch (err) {
            toast.error(err.message);
        } finally {
            setState(prev => ({
                ...prev,
                loadingId: INITIAL_STATE.loadingId
            }))
        }
    }

    const onEdit = (assessment) => {
        setState(prev => ({
            ...prev,
            draftAssessment: {...assessment}
        }))
    }

    const onChange = (name, value) => {
        const { draftAssessment } = state
        const [fName, sName] = name.split(".")
        let assessment = {
            ...draftAssessment,
            attr: {
                ...draftAssessment.attr,
                [fName]: {
                    ...draftAssessment.attr[fName],
                    [sName]: value
                }
            }
        }

        setState(prev => ({
            ...prev,
            draftAssessment: assessment
        }))
    }

    const onSave = async() => {
        const { draftAssessment, assessments } = state
        setState(prev => ({
            ...prev,
            loadingId: draftAssessment.assessmentId
        }))


        await updateFranchescaAssessment(draftAssessment)

        toast.success("Assessment has been updated!")

        setState((prev) => ({
            ...prev,
            assessments: assessments.map((item) => (item.assessmentId === draftAssessment.assessmentId ? { ...draftAssessment } : item)),
            draftAssessment: INITIAL_STATE.draftAssessment,
            loadingId: INITIAL_STATE.loadingId
        }))
    };

    const toggleFranchescaDropdownButton = () => setState(prev => ({
        ...prev,
        createFranchescaDropdownOpen: !state.createFranchescaDropdownOpen
    }))

    const createFranchescaAssessment = async(type) => {
        const { response } = await createFranchescaAssessmentsBySiteId(model.siteForm?.siteId, type)

        if(response){
            toast.success("Assessment was successfully created!")

            const { assessment } = response

            setState(prev => ({
                ...prev,
                assessments: [...state.assessments, assessment]
            }))
        }
    }

    useEffect(() => {
        if((prevModel?.siteForm?.siteId !== model.siteForm?.siteId) && model.siteForm?.siteId){
            getFranchescaAssessmentsBySiteId(model.siteForm?.siteId).then(({response, error}) => {
                if(response && !response.errorMessage){
                    setState(prev => ({
                        ...prev,
                        assessments: response.assessments
                    }))
                } else {
                    setState(prev => ({
                        ...prev,
                        assessments: INITIAL_STATE.assessments
                    }))
                }
            })
        }
    }, [prevModel?.siteForm?.siteId, model.siteForm?.siteId])

    const { loadingId, assessments, createFranchescaDropdownOpen, draftAssessment } = state

    return <>
        <h3>
            Franchesca Assessments <i className="fas fa-question-circle" style={{fontSize: "0.8em"}} onClick={() => runIntroTutorial()}></i>
        </h3><br />
        <div className="mb-4 d-flex flex-row-reverse">
            <ButtonDropdown size='lg' isOpen={createFranchescaDropdownOpen} toggle={toggleFranchescaDropdownButton} id="createAssessmentBtn">
                <DropdownToggle caret color='primary'>
                    Create Franchesca Assessment
                </DropdownToggle>
                <DropdownMenu>
                    <DropdownItem onClick={() => createFranchescaAssessment("INITIAL_SCAN")}>Initial Scan</DropdownItem>
                    <DropdownItem onClick={() => createFranchescaAssessment("SECRET_SHOPPING")}>Secret Shopping</DropdownItem>
                </DropdownMenu>
            </ButtonDropdown>
        </div>
        <div className="list-group">
            {assessments?.map((assessment) => (
                <FranchescaAssessmentListItem
                    key={assessment.assessmentId}
                    assessment={assessment}
                    draftAssessment={draftAssessment?.assessmentId === assessment.assessmentId ? draftAssessment : null}
                    onEditStart={onEdit}
                    onDownload={onDownload}
                    onSave={onSave}
                    onChange={onChange}
                    isLoading={loadingId === assessment.assessmentId}
                />
            ))}
        </div>
    </>
}

export default FranchescaAssessments